import { useCallback, useEffect, useRef, useState } from "react";
import { mintSuiteSsoUrl } from "@/lib/suite/openSuiteApp";
import { activityService } from "@/services/analytics/ActivityService";
import type { SuiteAppWiring } from "./types";

/**
 * useSuiteHandoff — the "sign in while they read" half of the explainer.
 *
 * The slow part of entering a satellite (Mail/Sync/Outbound) is minting the SSO
 * token and letting the satellite consume it. So we mint it in the background
 * the moment the explainer mounts, cache the resolved URL, and when the user
 * finishes (or skips) we hand off instantly with the pre-minted URL — no wait.
 * The token is re-minted only if it has gone stale.
 *
 * Opens in a new tab so the user keeps their place in Voice: `enterApp` resolves
 * `true` when the satellite went to a new tab, so the caller can send this tab
 * back to where the user came from.
 * The blank tab is opened synchronously inside the click handler (before any
 * `await`) to preserve the user gesture for iOS Safari, which silently blocks
 * `window.open()` calls made after an async hop — see openSuiteApp.ts.
 */

const SEEN_KEY = (appId: string) => `hg:suite-explainer-seen:${appId}`;

// SSO tokens are short-lived; a fresh pre-mint covers a normal read-through, but
// re-mint if the user lingered past this window before entering.
const TOKEN_STALE_MS = 45_000;

export const hasSeenExplainer = (appId: string): boolean => {
  try {
    return localStorage.getItem(SEEN_KEY(appId)) === "1";
  } catch {
    return false;
  }
};

export const markExplainerSeen = (appId: string): void => {
  try {
    localStorage.setItem(SEEN_KEY(appId), "1");
  } catch {
    /* private mode / storage disabled — non-fatal, they just see it again */
  }
};

export const useSuiteHandoff = ({ appId, productWord, appUrl, ssoEntryPath }: SuiteAppWiring) => {
  const minted = useRef<{ url: string; at: number } | null>(null);
  const inFlight = useRef<Promise<string> | null>(null);
  const [ready, setReady] = useState(false);
  const [entering, setEntering] = useState(false);

  const warm = useCallback((): Promise<string> => {
    const p = mintSuiteSsoUrl(appUrl, ssoEntryPath).then((url) => {
      minted.current = { url, at: Date.now() };
      setReady(true);
      return url;
    });
    inFlight.current = p;
    return p;
  }, [appUrl, ssoEntryPath]);

  // Pre-warm the SSO token as soon as the explainer appears.
  useEffect(() => {
    void warm();
  }, [warm]);

  const enterApp = useCallback(
    async (source: string): Promise<boolean> => {
      setEntering(true);
      activityService.logSuiteAppVisit(productWord, appUrl, source);
      markExplainerSeen(appId);

      // Open synchronously to preserve the user gesture for iOS Safari.
      const win = window.open("about:blank", "_blank");
      if (win) {
        try {
          win.opener = null;
        } catch {
          /* cross-origin guard — safe to ignore */
        }
      }

      const cached = minted.current;
      let url: string;
      if (cached && Date.now() - cached.at < TOKEN_STALE_MS) {
        url = cached.url; // fresh pre-mint — instant handoff
      } else if (!cached && inFlight.current) {
        url = await inFlight.current; // first mint still resolving — wait it out
      } else {
        url = await warm(); // nothing minted yet, or the token went stale — mint fresh
      }

      if (win) {
        win.location.href = url;
        return true;
      }
      window.location.href = url; // popup blocked — fall back to current tab
      return false;
    },
    [appId, productWord, appUrl, warm],
  );

  return { ready, entering, enterApp };
};
